import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
  untracked,
} from '@angular/core';
import { Step } from '@softline/application';
import { ResolvableModal } from '@softline/ui-core';
import { ProgressStepsComponent } from '@softline/ui-core/progress';
import { SalesConfigService } from '../../services/sales-config.service';
import {
  DocumentFormData,
  EMPTY_DOCUMENT_FORM_DATA,
} from '../../types/document-form-data';
import { ConstructionSiteStepComponent } from './steps/construction-site-step/construction-site-step.component';
import { CustomerStepComponent } from './steps/customer-step/customer-step.component';
import { DeliveryTypeStepComponent } from './steps/delivery-type-step/delivery-type-step.component';

@Component({
  selector: 'soft-document-creation-wizard',
  templateUrl: './document-creation-wizard.component.html',
  styleUrl: './document-creation-wizard.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'flex flex-col' },
  imports: [
    ProgressStepsComponent,
    CustomerStepComponent,
    ConstructionSiteStepComponent,
    DeliveryTypeStepComponent,
  ],
})
export class DocumentCreationWizardComponent extends ResolvableModal<DocumentFormData> {
  private readonly deviceConfig = inject(SalesConfigService).config();

  readonly initialCustomer = input<DocumentFormData['customer'] | null>(null);
  /** Pre-selected construction site id (e.g. clicked from the customer side
   *  panel). When set, the Baustelle step is pre-filled and the wizard opens on
   *  the Lieferart step. */
  readonly initialConstructionSiteId = input<number | null>(null);

  readonly navTabIndex = signal(0);

  readonly documentData = signal<DocumentFormData>({
    ...EMPTY_DOCUMENT_FORM_DATA,
    distributionTypeId: this.deviceConfig.defaultDistributionType
      ? Number(this.deviceConfig.defaultDistributionType)
      : null,
  });

  private readonly _seedFromInitialCustomer = effect(() => {
    const customer = this.initialCustomer();
    if (!customer) return;
    const constructionSiteId = untracked(() => this.initialConstructionSiteId());
    untracked(() => {
      this.documentData.update((d) => ({ ...d, customer, constructionSiteId }));
      // With a pre-selected site, skip ahead to Lieferart; otherwise land on
      // the Baustelle step as before.
      this.navTabIndex.set(constructionSiteId != null ? 2 : 1);
    });
  });

  readonly isPickup = signal(false);

  onDeliveryTypeChange(data: DocumentFormData): void {
    this.documentData.set(data);
    this.isPickup.set(data.deliveryType?.toLowerCase().includes('abholung') ?? false);
  }

  readonly steps = computed<Step[]>(() => {
    const d = this.documentData();
    return [
      {
        header: 'Kunde',
        subHeader: d.customer
          ? [d.customer.name1, d.customer.name2]
              .filter(Boolean)
              .join(' ')
          : undefined,
        done: !!d.customer,
        execute: () => this.navTabIndex.set(0),
      },
      {
        header: 'Baustelle',
        subHeader:
          d.constructionSite ??
          (d.constructionSiteId != null ? 'Baustelle gewählt' : undefined),
        done: !!d.constructionSite || d.constructionSiteId != null,
        execute: () => this.navTabIndex.set(1),
      },
      {
        header: 'Lieferart',
        subHeader: d.deliveryType ?? undefined,
        done: !!d.deliveryType,
        execute: () => this.navTabIndex.set(2),
      },
    ];
  });

  onNext(data: DocumentFormData): void {
    this.documentData.set(data);
    if (this.navTabIndex() >= 2) {
      this.submit();
    } else {
      this.navTabIndex.update((i) => i + 1);
    }
  }

  onCreate(data: DocumentFormData): void {
    this.documentData.set(data);
    this.submit();
  }

  submit(): void {
    this.resolve(this.documentData());
  }
}
